import { supabase } from "@/integrations/supabase/client";
import type { ContentType } from "@/types/content";

const DOCUMENT_TYPES: ContentType[] = ["pdf", "doc", "ppt", "web", "text"];
const MEDIA_TYPES: ContentType[] = ["youtube", "audio", "video", "recording"];

const DEFAULT_MAX_CHARS = 150_000;

/**
 * Format seconds as MM:SS for transcript markers.
 */
function formatMmSs(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Fetches the source text for content from document_text or media_transcripts tables.
 * Used for AI context grounding in flashcards, quizzes, summaries, and chat.
 *
 * @param contentId - The ID of the content
 * @param contentType - The type of content (pdf, doc, youtube, etc.)
 * @returns The concatenated text content, limited to ~150k characters, or null if not available
 */
export async function getContentSourceText(
  contentId: string,
  contentType: ContentType
): Promise<string | null> {
  if (DOCUMENT_TYPES.includes(contentType)) {
    const { data, error } = await supabase
      .from("document_text")
      .select("page_number, text")
      .eq("content_id", contentId)
      .order("page_number", { ascending: true });

    if (error) {
      console.error("Error fetching document text:", error);
      return null;
    }
    if (!data?.length) return null;

    const fullText = data.map((p) => p.text).join("\n\n");
    return fullText.slice(0, DEFAULT_MAX_CHARS);
  }

  if (MEDIA_TYPES.includes(contentType)) {
    const { data, error } = await supabase
      .from("media_transcripts")
      .select("start_seconds, text")
      .eq("content_id", contentId)
      .order("start_seconds", { ascending: true });

    if (error) {
      console.error("Error fetching media transcripts:", error);
      return null;
    }
    if (!data?.length) return null;

    const fullText = data.map((s) => s.text).join(" ");
    return fullText.slice(0, DEFAULT_MAX_CHARS);
  }

  return null;
}

export interface AnchoredSourceResult {
  /** Full text with [Page N] / [MM:SS] markers inline. */
  text: string;
  /** True if the content has page anchors (documents). */
  hasPageAnchors: boolean;
  /** True if the content has timestamp anchors (media). */
  hasTimeAnchors: boolean;
}

/**
 * Fetches the source text **with inline anchors** that the model can cite.
 *
 * - Documents → `[Page N]\n${pageText}` joined with `\n\n`.
 * - Media     → `[MM:SS] ${segmentText}` joined with `\n`.
 *
 * The format mirrors what `generate-exam` already injects, so the model
 * always sees citation-ready material across every grounded surface.
 */
export async function getAnchoredContentSourceText(
  contentId: string,
  contentType: ContentType,
  opts: { maxChars?: number } = {}
): Promise<AnchoredSourceResult | null> {
  const maxChars = opts.maxChars ?? DEFAULT_MAX_CHARS;

  if (DOCUMENT_TYPES.includes(contentType)) {
    const { data, error } = await supabase
      .from("document_text")
      .select("page_number, text")
      .eq("content_id", contentId)
      .order("page_number", { ascending: true });

    if (error) {
      console.error("Error fetching document text (anchored):", error);
      return null;
    }
    if (!data?.length) return null;

    const anchored = data
      .map((p) => `[Page ${p.page_number}]\n${p.text ?? ""}`)
      .join("\n\n");
    return {
      text: anchored.slice(0, maxChars),
      hasPageAnchors: true,
      hasTimeAnchors: false,
    };
  }

  if (MEDIA_TYPES.includes(contentType)) {
    const { data, error } = await supabase
      .from("media_transcripts")
      .select("start_seconds, text")
      .eq("content_id", contentId)
      .order("start_seconds", { ascending: true });

    if (error) {
      console.error("Error fetching media transcripts (anchored):", error);
      return null;
    }
    if (!data?.length) return null;

    const anchored = data
      .map((s) => `[${formatMmSs(s.start_seconds)}] ${s.text ?? ""}`)
      .join("\n");
    return {
      text: anchored.slice(0, maxChars),
      hasPageAnchors: false,
      hasTimeAnchors: true,
    };
  }

  return null;
}

/**
 * Standardized inline-citation instruction for grounded prompts.
 * Models emit `[[p:N]]` for document pages and `[[t:SS]]` for media seconds;
 * the client renders them as clickable badges that jump the viewer.
 *
 * Pass a `docId` qualifier to disambiguate multi-document chats:
 * `[[p:N|doc:UUID]]` / `[[t:SS|doc:UUID]]`.
 */
export function buildCitationInstruction(opts: {
  hasPageAnchors: boolean;
  hasTimeAnchors: boolean;
  multiDoc?: boolean;
}): string {
  if (!opts.hasPageAnchors && !opts.hasTimeAnchors) return "";

  const lines: string[] = ["## Source Citations"];
  lines.push(
    "The study material below is annotated with anchors. When you draw on a passage, cite it inline using EXACTLY this token format:"
  );
  if (opts.hasPageAnchors) {
    lines.push(
      opts.multiDoc
        ? '- For document pages: `[[p:N|doc:UUID]]` (N from a `[Page N]` marker, UUID from the `[doc:UUID]` header above the source)'
        : "- For document pages: `[[p:N]]` (N is the page number from a `[Page N]` marker)"
    );
  }
  if (opts.hasTimeAnchors) {
    lines.push(
      opts.multiDoc
        ? '- For media timestamps: `[[t:SS|doc:UUID]]` (SS = integer seconds from a `[MM:SS]` marker, converted to seconds)'
        : "- For media timestamps: `[[t:SS]]` (SS = integer seconds, converted from a `[MM:SS]` marker)"
    );
  }
  lines.push(
    "- Place the citation IMMEDIATELY after the sentence it supports.",
    "- Multiple citations may be chained: `...[[p:3]][[p:7]]`.",
    "- Never invent a page or timestamp that is not in the source. If unsure, omit the citation.",
    "- Do NOT cite in headings, code blocks, or diagram blocks.",
    "- Always prefer page or timestamp citations. Do NOT emit a bare `[[doc:UUID]]` token — it carries no anchor and clutters the reply."
  );
  return lines.join("\n");
}

/**
 * Check if content type is a document (has document_text)
 */
export function isDocumentType(contentType: ContentType): boolean {
  return DOCUMENT_TYPES.includes(contentType);
}

/**
 * Check if content type is media (has media_transcripts)
 */
export function isMediaType(contentType: ContentType): boolean {
  return MEDIA_TYPES.includes(contentType);
}
